import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Package, Pencil, Plus, ShieldAlert, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Switch } from '@/components/ui/Switch';
import { Textarea } from '@/components/ui/Textarea';
import { WorkItemPicker } from '@/components/work/WorkItemPicker';
import { useConfigureWorkstation } from '@/features/workMode/hooks';
import { useStationItemIds, useSetStationItems } from '@/features/workMode/stationSetup';
import { describeWorkError, durationInputText, parseDurationInput } from '@/features/workMode/api';
import { useUpdateWorkstation } from '@/features/inventory/hooks';
import type { WorkLookups } from '@/features/workMode/useLookups';
import type { FloorWorkstation } from '@/features/workMode/types';

interface StationOverviewTabProps {
  station: FloorWorkstation;
  lookups: WorkLookups;
  canManage: boolean;
  /** Super admin / admin: may rename the station (the name is admin-curated). */
  canRename: boolean;
}

const PPE_SUGGESTIONS = [
  'Safety glasses',
  'Cut-resistant gloves',
  'Nitrile gloves',
  'Hearing protection',
  'Steel-toe boots',
  'Respirator',
  'Face shield',
  'Apron',
];

/**
 * The basics plus everything that used to have no home: location, safety
 * notes, PPE, general notes, and the items built at this bench. One Save
 * for the form; the items list saves on its own.
 */
export function StationOverviewTab({
  station,
  lookups,
  canManage,
  canRename,
}: StationOverviewTabProps) {
  const configure = useConfigureWorkstation();
  const rename = useUpdateWorkstation();

  const [name, setName] = useState(station.name);
  const [description, setDescription] = useState(station.description ?? '');
  const [target, setTarget] = useState(durationInputText(station.target_seconds));
  const [itemId, setItemId] = useState<string | null>(station.default_item_id);
  const [isActive, setIsActive] = useState(station.is_active);
  const [location, setLocation] = useState(station.location_notes ?? '');
  const [safety, setSafety] = useState(station.safety_notes ?? '');
  const [ppe, setPpe] = useState<string[]>(station.ppe ?? []);
  const [ppeDraft, setPpeDraft] = useState('');
  const [notes, setNotes] = useState(station.setup_notes ?? '');
  const [pickingDefault, setPickingDefault] = useState(false);

  // A background refetch (realtime, another tab) must not wipe edits, so
  // only resync when the row itself changed under us.
  useEffect(() => {
    setName(station.name);
    setDescription(station.description ?? '');
    setTarget(durationInputText(station.target_seconds));
    setItemId(station.default_item_id);
    setIsActive(station.is_active);
    setLocation(station.location_notes ?? '');
    setSafety(station.safety_notes ?? '');
    setPpe(station.ppe ?? []);
    setNotes(station.setup_notes ?? '');
  }, [station.updated_at, station.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const parsedTarget = parseDurationInput(target);
  const samePpe =
    ppe.length === (station.ppe ?? []).length && ppe.every((p, i) => p === (station.ppe ?? [])[i]);
  const dirty =
    name.trim() !== station.name ||
    description !== (station.description ?? '') ||
    target !== durationInputText(station.target_seconds) ||
    itemId !== station.default_item_id ||
    isActive !== station.is_active ||
    location !== (station.location_notes ?? '') ||
    safety !== (station.safety_notes ?? '') ||
    !samePpe ||
    notes !== (station.setup_notes ?? '');

  const addPpe = (value: string) => {
    const v = value.trim();
    if (!v) return;
    if (ppe.some((p) => p.toLowerCase() === v.toLowerCase())) {
      setPpeDraft('');
      return;
    }
    setPpe([...ppe, v]);
    setPpeDraft('');
  };

  const save = async () => {
    if (parsedTarget === 'invalid') {
      toast.error('Target looks odd — use mm:ss or minutes, e.g. 12:30 or 12.5.');
      return;
    }
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error('The station needs a name.');
      return;
    }
    try {
      if (canRename && trimmedName !== station.name) {
        await rename.mutateAsync({ id: station.id, patch: { name: trimmedName } });
      }
      await configure.mutateAsync({
        id: station.id,
        description: description.trim(),
        targetSeconds: parsedTarget,
        defaultItemId: itemId,
        isActive,
        locationNotes: location.trim(),
        safetyNotes: safety.trim(),
        setupNotes: notes.trim(),
        ppe,
      });
      toast.success(`${trimmedName} saved.`);
    } catch (e) {
      toast.error(describeWorkError(e));
    }
  };

  const clearPlacement = () => {
    configure.mutate(
      { id: station.id, floor: null },
      {
        onSuccess: () => toast.success('Removed from the map.'),
        onError: (e) => toast.error(describeWorkError(e)),
      },
    );
  };

  const busy = configure.isPending || rename.isPending;

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]">
      <div className="space-y-4">
        <section className="rounded-2xl border border-border bg-card p-5">
          <h3 className="text-sm font-medium text-foreground">Basics</h3>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="ws-name">Name</Label>
              <Input
                id="ws-name"
                value={name}
                maxLength={64}
                onChange={(e) => setName(e.target.value)}
                disabled={!canRename}
              />
              {!canRename && canManage && (
                <p className="text-xs text-muted-foreground">
                  Only admins rename stations; the name is shared with the catalog.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ws-desc">What happens here</Label>
              <Input
                id="ws-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g. Base sub-assembly, bench 2"
                disabled={!canManage}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Default item</Label>
              <div className="flex items-center gap-2">
                <div className="flex min-h-10 flex-1 items-center rounded-lg border border-border bg-background px-3 text-sm">
                  {itemId ? (
                    <span className="truncate">
                      {lookups.itemName(itemId)}{' '}
                      <span className="text-muted-foreground">{lookups.itemSku(itemId)}</span>
                    </span>
                  ) : (
                    <span className="text-muted-foreground">Ask the worker each shift</span>
                  )}
                </div>
                {canManage && (
                  <Button variant="outline" onClick={() => setPickingDefault(true)}>
                    Choose
                  </Button>
                )}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ws-target">Target time per unit</Label>
              <Input
                id="ws-target"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                placeholder="mm:ss, e.g. 14:00"
                className={cn(parsedTarget === 'invalid' && 'border-destructive')}
                disabled={!canManage}
              />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="ws-location">Where it is</Label>
              <Input
                id="ws-location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="North wall, left of the press — look for the blue tray rack"
                disabled={!canManage}
              />
            </div>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <div className="flex items-center justify-between rounded-xl border border-border px-4 py-3">
              <div>
                <p className="text-sm font-medium text-foreground">Station is active</p>
                <p className="text-xs text-muted-foreground">
                  Switched-off stations keep their history but disappear from the tablets.
                </p>
              </div>
              <Switch checked={isActive} onCheckedChange={setIsActive} disabled={!canManage} />
            </div>
            <div className="flex items-center justify-between gap-3 rounded-xl border border-border px-4 py-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">On Digital Company</p>
                <p className="truncate text-xs text-muted-foreground">
                  {station.floor_x != null && station.floor_y != null
                    ? `Placed at ${Math.round(station.floor_x)}, ${Math.round(station.floor_y)}.`
                    : 'Not placed on the map yet.'}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Button asChild variant="outline" size="sm">
                  <Link to="/digital-company?arrange=1">
                    <MapPin className="h-4 w-4" />
                    Arrange
                  </Link>
                </Button>
                {canManage && station.floor_x != null && (
                  <Button variant="ghost" size="sm" onClick={clearPlacement} disabled={busy}>
                    Clear
                  </Button>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-fresh-salmon-dark" />
            <h3 className="text-sm font-medium text-foreground">Safety</h3>
          </div>
          <div className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label>Required PPE</Label>
              <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-border bg-background p-2">
                {ppe.map((p) => (
                  <span
                    key={p}
                    className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-xs font-medium text-foreground"
                  >
                    {p}
                    {canManage && (
                      <button
                        type="button"
                        onClick={() => setPpe(ppe.filter((x) => x !== p))}
                        aria-label={`Remove ${p}`}
                        className="rounded-full text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </span>
                ))}
                {canManage && (
                  <input
                    value={ppeDraft}
                    onChange={(e) => setPpeDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ',') {
                        e.preventDefault();
                        addPpe(ppeDraft);
                      } else if (e.key === 'Backspace' && !ppeDraft && ppe.length) {
                        setPpe(ppe.slice(0, -1));
                      }
                    }}
                    onBlur={() => addPpe(ppeDraft)}
                    placeholder={
                      ppe.length ? 'Add another…' : 'Safety glasses, gloves… (Enter to add)'
                    }
                    className="min-w-[160px] flex-1 bg-transparent px-1 py-1 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
                  />
                )}
                {!canManage && ppe.length === 0 && (
                  <span className="px-1 text-sm text-muted-foreground">None listed</span>
                )}
              </div>
              {canManage && (
                <div className="flex flex-wrap gap-1">
                  {PPE_SUGGESTIONS.filter(
                    (s) => !ppe.some((p) => p.toLowerCase() === s.toLowerCase()),
                  ).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => addPpe(s)}
                      className="rounded-full border border-dashed border-border px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-secondary hover:text-foreground"
                    >
                      + {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ws-safety">Hazards and rules</Label>
              <Textarea
                id="ws-safety"
                value={safety}
                onChange={(e) => setSafety(e.target.value)}
                rows={3}
                placeholder="Pinch point on the press — keep both hands on the handles. Hot plate stays on; do not touch the silver surface."
                disabled={!canManage}
              />
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-border bg-card p-5">
          <h3 className="text-sm font-medium text-foreground">Anything else</h3>
          <div className="mt-4 space-y-1.5">
            <Label htmlFor="ws-notes">Notes for whoever runs this station</Label>
            <Textarea
              id="ws-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="Restock the fastener tray from bin C-12 at the start of each shift. The air line valve is behind the bench."
              disabled={!canManage}
            />
          </div>
        </section>

        {canManage && (
          <div className="flex items-center justify-end gap-3">
            {dirty && <p className="text-xs text-muted-foreground">Unsaved changes</p>}
            <Button onClick={() => void save()} disabled={!dirty || busy}>
              {busy ? 'Saving…' : 'Save'}
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-4">
        <BuildsHereCard
          station={station}
          lookups={lookups}
          canManage={canManage}
          defaultItemId={itemId}
        />
      </div>

      <WorkItemPicker
        open={pickingDefault}
        onOpenChange={setPickingDefault}
        stationId={station.id}
        currentItemId={itemId}
        onPick={setItemId}
        title="Default item for this station"
        allowNone
      />
    </div>
  );
}

/** The items linked to this station through item_workstations — what the tablet floats to the top and what sign-offs copy instructions for. */
function BuildsHereCard({
  station,
  lookups,
  canManage,
  defaultItemId,
}: {
  station: FloorWorkstation;
  lookups: WorkLookups;
  canManage: boolean;
  defaultItemId: string | null;
}) {
  const { data: itemIds = [], isLoading } = useStationItemIds(station.id);
  const setItems = useSetStationItems();
  const [picking, setPicking] = useState(false);

  const rows = useMemo(
    () =>
      itemIds
        .map((id) => ({
          id,
          name: lookups.itemName(id) ?? 'Unknown item',
          sku: lookups.itemSku(id) ?? '',
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [itemIds, lookups],
  );
  const defaultMissing = !!defaultItemId && !itemIds.includes(defaultItemId);

  const update = (next: string[], done: string) => {
    setItems.mutate(
      { stationId: station.id, itemIds: next },
      {
        onSuccess: () => toast.success(done),
        onError: (e) => toast.error(describeWorkError(e)),
      },
    );
  };

  return (
    <section className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-medium text-foreground">Built here</h3>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            These items float to the top of the picker on the tablet, and their published
            instructions count as this station's instructions.
          </p>
        </div>
        {canManage && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setPicking(true)}
            disabled={setItems.isPending}
          >
            <Plus className="h-4 w-4" />
            Add item
          </Button>
        )}
      </div>

      {isLoading ? (
        <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="mt-4 rounded-xl border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
          No items linked yet.
        </p>
      ) : (
        <ul className="mt-4 divide-y divide-border">
          {rows.map((r) => (
            <li key={r.id} className="flex items-center gap-3 py-2.5">
              <div className="min-w-0 flex-1">
                <Link
                  to={`/catalog/items/${r.id}`}
                  className="truncate text-sm font-medium text-foreground hover:underline"
                >
                  {r.name}
                </Link>
                <p className="truncate text-xs text-muted-foreground">{r.sku}</p>
              </div>
              {r.id === defaultItemId && <Badge variant="info-soft">Default</Badge>}
              {canManage && (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Unlink ${r.name}`}
                  disabled={setItems.isPending}
                  onClick={() =>
                    update(
                      itemIds.filter((id) => id !== r.id),
                      `${r.name} unlinked.`,
                    )
                  }
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {defaultMissing && canManage && (
        <button
          type="button"
          onClick={() => update([...itemIds, defaultItemId!], 'Default item linked.')}
          className="mt-3 flex w-full items-center gap-2 rounded-xl border border-dashed border-info-soft bg-info-soft/15 px-3 py-2 text-left text-xs text-info hover:bg-info-soft/30"
        >
          <Pencil className="h-3.5 w-3.5 shrink-0" />
          The default item ({lookups.itemName(defaultItemId)}) is not in this list — tap to link it.
        </button>
      )}

      <WorkItemPicker
        open={picking}
        onOpenChange={setPicking}
        stationId={station.id}
        currentItemId={null}
        title="Which item is built at this station?"
        onPick={(id) => {
          if (!id || itemIds.includes(id)) return;
          update([...itemIds, id], `${lookups.itemName(id)} linked.`);
        }}
      />
    </section>
  );
}
